import { useState } from 'react';

import { DocumentFileUpload } from '@backrs_ui/common-ui/src/molecules/Profile/DocumentFileUpload';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { DocumentType, DOCUMENT_NAMES, DOCUMENT_TYPES } from '@backrs_ui/grow-ui/src/models/models';
import {
  getUnviewableLinkMessage,
  isValidUrl,
} from '@backrs_ui/grow-ui/src/pages/Profile/utils/linkDocument.util';
import {
  AttachedFilePayload,
  IngestLinkResponse,
  useIngestLinkMutation,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { profileApi, StoryDocument } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import {
  selectIsSharedAccountSessionActive,
  selectSharedAccountSessionToken,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedAccountSession';
import { store } from '@backrs_ui/grow-ui/src/redux/store';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { fetchWrapper } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';
import { uploadFile } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import { SxProps, Theme, Box, TextField, CircularProgress, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

import { EnterButton } from './EnterButton';
import { FILE_CONFIG, getFileType } from './fileUpload.util';

const { fileUpload } = appStrings.modules.chatBox;

type FileState = Readonly<{
  names: string[];
  files: File[];
}>;

type DocumentParsingState = Readonly<{
  text: string;
  mediaIds: number[];
  s3Keys: string[];
  documentTypes: (DocumentType | null)[];
}>;

type SourceFields = Pick<StoryDocument, 'source_url' | 'refresh_policy' | 'viewable'>;

type FileUploadProps = Readonly<{
  userId: number;
  onSubmit: (fileNames: string[], content?: string, attachments?: AttachedFilePayload[]) => void;
  onError?: (message?: string) => void;
  cancelComponent?: React.ReactNode;
  addToProfile?: boolean;
  documentType?: DocumentType;
}>;

export const FileUpload = ({
  userId,
  onSubmit,
  onError,
  cancelComponent,
  addToProfile,
  documentType,
}: FileUploadProps) => {
  const [link, setLink] = useState('');
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<FileState>({ names: [], files: [] });
  const [isLoading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAttachingToProfile, setIsAttachingToProfile] = useState(false);
  const [parsedDocument, setParsedDocument] = useState<DocumentParsingState>({
    text: '',
    mediaIds: [],
    s3Keys: [],
    documentTypes: [],
  });

  const dispatch = useAppDispatch();
  const [ingestLink] = useIngestLinkMutation();

  const processDocument = async ({
    file,
    s3_key,
    classify_as,
  }: {
    file: File;
    s3_key: string;
    classify_as?: string[];
  }) => {
    const formData = new FormData();

    formData.append('file', file);

    const searchParams = new URLSearchParams();

    searchParams.append('s3_key', s3_key);
    if (classify_as) {
      classify_as.forEach((type) => {
        searchParams.append('classify_as', type);
      });
    }

    const state = store.getState();
    const isSharedAccountSessionActive = selectIsSharedAccountSessionActive(state);
    const sharedAccountSessionToken = selectSharedAccountSessionToken(state);

    const token =
      isSharedAccountSessionActive && sharedAccountSessionToken
        ? sharedAccountSessionToken
        : getBackrsToken();

    const response = await fetch(
      `${process.env.REACT_APP_LUBAV_API_BASEURL}/api/process-document?${searchParams.toString()}`,
      {
        method: 'POST',
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          accept: 'application/json',
        },
        body: formData,
      },
    );

    if (!response.ok) {
      throw new Error('Failed to process document');
    }

    return response.json();
  };

  const allowedExtensions = Object.values(FILE_CONFIG).flatMap((config) =>
    config.extension.split(','),
  );

  const attachDocumentToProfile = async ({
    title,
    mediaType,
    docType,
    mediaId,
    sourceFields,
  }: {
    title: string;
    mediaType: StoryDocument['media_type'];
    docType: DocumentType | null;
    mediaId: number;
    sourceFields?: SourceFields;
  }) => {
    const profilePayload = {
      user_id: userId,
      category_id: 'story',
      visibility: 'panel', // assume we don't want it to default to public
      question_id: 'document',
      option_id: documentType ?? docType ?? 'other',
      title,
      media_id: mediaId,
      media_type: mediaType,
      ...(sourceFields ?? {}),
    };

    const profileResponse = await fetchWrapper(`${ENVIRONMENT_URL}/users-profile-answers`, {
      method: 'POST',
      body: JSON.stringify(profilePayload),
    });

    if (!profileResponse.ok) {
      throw new Error('Failed to create profile answer');
    }

    // invalidate user profile to force the profile page to refetch
    dispatch(profileApi.util.invalidateTags(['UserProfile']));
  };

  const resetState = () => {
    setUploadedFile({ names: [], files: [] });
    setIsUploading(false);
    setParsedDocument({
      text: '',
      mediaIds: [],
      s3Keys: [],
      documentTypes: [],
    });
  };

  const processFile = async (files: File | File[]) => {
    const filesArray = Array.isArray(files) ? files : [files];

    if (!files || filesArray.length === 0) return;

    setIsUploading(true);

    try {
      const processedFiles: File[] = [];
      const fileNames: string[] = [];
      const mediaIds: number[] = [];
      const s3Keys: string[] = [];
      const documentTypes: (DocumentType | null)[] = [];
      const texts: string[] = [];

      for (const file of filesArray) {
        const { mediaId, s3Key } = await uploadFile({ file });

        const processResponse = await processDocument({
          file,
          s3_key: s3Key,
          ...(!documentType ? { classify_as: Object.values(DOCUMENT_TYPES) } : {}),
        });

        processedFiles.push(file);
        fileNames.push(file.name);
        mediaIds.push(mediaId);
        s3Keys.push(s3Key);
        documentTypes.push(processResponse.document_type ?? null);
        texts.push(processResponse.text || '');
      }

      setUploadedFile({ names: fileNames, files: processedFiles });
      setParsedDocument({
        text: texts.join('\n\n'),
        mediaIds,
        s3Keys,
        documentTypes,
      });
    } catch (error) {
      console.error('Error processing document:', error);

      resetState();
      onError?.();
    } finally {
      setIsUploading(false);
    }
  };

  const processLink = async (url: string): Promise<IngestLinkResponse | null> => {
    try {
      setIsUploading(true);

      const result = await ingestLink({
        url,
        ...(documentType ? {} : { classify_as: Object.values(DOCUMENT_TYPES) }),
      }).unwrap();

      if (!result.viewable || !result.text) {
        onError?.(getUnviewableLinkMessage(result, 'I'));
        resetState();

        return null;
      }

      return result;
    } catch (error) {
      console.error('Error ingesting link:', error);
      resetState();
      onError?.();

      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const attachToProfileAndSubmit = async (
    files: File | File[],
    text: string,
    mediaIds?: number | number[],
    documentTypes?: (DocumentType | null)[],
  ) => {
    const filesArray = Array.isArray(files) ? files : [files];
    const mediaIdsArray = Array.isArray(mediaIds) ? mediaIds : mediaIds ? [mediaIds] : [];

    if (addToProfile && filesArray.length > 0 && mediaIdsArray.length > 0) {
      setIsAttachingToProfile(true);
      try {
        for (let i = 0; i < filesArray.length; i++) {
          const fileDocType = documentTypes?.[i] || documentType;

          if (fileDocType) {
            await attachDocumentToProfile({
              title: filesArray[i].name,
              mediaType: getFileType(filesArray[i].type),
              docType: fileDocType,
              mediaId: mediaIdsArray[i],
            });
          }
        }
      } catch (error) {
        console.error('Error attaching documents to profile:', error);
        onError?.();

        return false;
      } finally {
        setIsAttachingToProfile(false);
      }
    }

    const fileNames = filesArray.map((f) => f.name);
    const attachments: AttachedFilePayload[] = filesArray.map((f, i) => ({
      s3_key: parsedDocument.s3Keys[i],
      filename: f.name,
      mime_type: f.type,
    }));

    onSubmit(fileNames, text, attachments);

    return true;
  };

  const submitLink = async (url: string) => {
    const result = await processLink(url);

    if (!result) return;

    const docType = result.document_type ?? null;
    // A fetched link has no meaningful filename, so title it by the document type.
    const type = documentType ?? docType;
    const typeName = type ? DOCUMENT_NAMES[type] : undefined;
    const title = typeName ? typeName.charAt(0).toUpperCase() + typeName.slice(1) : url;
    const mediaType = result.media_type ?? 'link';

    if (addToProfile && result.media_id) {
      setIsAttachingToProfile(true);
      try {
        await attachDocumentToProfile({
          title,
          mediaType,
          docType,
          mediaId: result.media_id,
          sourceFields: {
            source_url: result.source_url ?? url,
            refresh_policy: result.refresh_policy,
            viewable: 'true',
          },
        });
      } catch (error) {
        console.error('Error attaching document to profile:', error);
        onError?.();

        return;
      } finally {
        setIsAttachingToProfile(false);
      }
    }

    const linkAttachment: AttachedFilePayload = {
      filename: title,
      mime_type: result.content_type ?? 'text/plain',
      source_url: result.source_url ?? url,
    };

    onSubmit([title], result.text ?? undefined, [linkAttachment]);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (link && uploadedFile.files.length === 0 && isLinkValid) {
        await submitLink(link);

        return;
      }

      if (uploadedFile.names.length > 0 && uploadedFile.files.length > 0 && parsedDocument.text) {
        await attachToProfileAndSubmit(
          uploadedFile.files,
          parsedDocument.text,
          parsedDocument.mediaIds,
          parsedDocument.documentTypes,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={fileUploadContainerStyle} onKeyPress={handleKeyPress}>
      <DocumentFileUpload
        onFileSelect={processFile}
        onError={(error) => {
          console.error('Error from DocumentFileUpload:', error);
          onError?.();
        }}
        acceptedFileTypes={allowedExtensions}
        maxFileSize={10 * 1024 * 1024} // 10MB
        inputId="file-upload"
        multiple={true}
      >
        {uploadedFile.names.length > 0 && (
          <Box sx={uploadedFilesContainerStyle}>
            <Typography variant="text-sm-regular">{uploadedFile.names.join(', ')}</Typography>
          </Box>
        )}
      </DocumentFileUpload>

      <Box sx={linkContainerStyle}>
        <TextField
          id="file-upload-document-link-input"
          fullWidth
          placeholder={fileUpload.pasteDocumentLink}
          value={link}
          onChange={(e) => {
            setLink(e.target.value);
            setIsLinkValid(isValidUrl(e.target.value));
          }}
          sx={linkFieldStyle}
          disabled={uploadedFile.names.length > 0 || isSubmitting}
          error={link.trim() !== '' && !isLinkValid}
          helperText={link.trim() !== '' && !isLinkValid ? fileUpload.invalidLink : ''}
        />
      </Box>

      <Box sx={submitContainerStyle}>
        <Box sx={submitButtonContainerStyle}>
          <EnterButton
            onClick={handleSubmit}
            label={fileUpload.submit}
            disabled={
              (uploadedFile.names.length === 0 && (!link || !isLinkValid)) ||
              isLoading ||
              isAttachingToProfile ||
              isSubmitting
            }
            showEnterHint
          />
          {isSubmitting && <CircularProgress size={20} sx={animationStyle} />}
        </Box>
        {cancelComponent}
      </Box>
    </Box>
  );
};

const fileUploadContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: backrs.spacing['spacing-2xl'],
  animation: `${keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `} 0.3s ease-out`,
});

const submitContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-2xl'],
  alignItems: 'center',
  justifyContent: 'space-between',
});

const submitButtonContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-2xl'],
});

const linkContainerStyle: SxProps<Theme> = {
  width: '100%',
};

const linkFieldStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: backrs.radius['radius-md'],
    ...typography['text-md-regular'],
  },
});

const animationStyle: React.CSSProperties = {
  height: '20px',
  width: '20px',
};

const uploadedFilesContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  alignItems: 'center',
  justifyContent: 'center',
  color: backrs.colors['Gray/700'],
  height: '126px',
});
